import { CheckCircleOutlined, Star, StarBorder } from '@mui/icons-material';
import { Box, Chip, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { getPlaceholdersForType } from 'config/placeholders';
import useTranslate from 'hooks/useTranslate';
import type { PlaceholderDefinition, TemplateType } from 'types';

interface VariablesStatusBarProps {
    htmlContent: string;
    onInsert: (token: string, mandatory: boolean) => void;
    foundPlaceholders?: string[];
    templateType?: TemplateType;
}

const COPIED_RESET_MS = 1600;

const visuallyHiddenSx = {
    position: 'absolute',
    width: 1,
    height: 1,
    p: 0,
    m: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
} as const;

export function VariablesStatusBar({
    htmlContent,
    onInsert,
    foundPlaceholders = [],
    templateType = 'visa-application',
}: VariablesStatusBarProps) {
    const { translate } = useTranslate();
    const [copiedToken, setCopiedToken] = useState<string | null>(null);
    const [copyFailedToken, setCopyFailedToken] = useState<string | null>(null);
    const copiedTimer = useRef<number | null>(null);
    const placeholders = getPlaceholdersForType(templateType);

    useEffect(() => {
        return () => {
            if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
        };
    }, []);

    const isVariableUsed = (token: string): boolean => {
        if (foundPlaceholders.length > 0) {
            return foundPlaceholders.includes(token);
        }
        return htmlContent.includes(token);
    };

    const showCopyFeedback = (token: string, failed: boolean) => {
        setCopiedToken(failed ? null : token);
        setCopyFailedToken(failed ? token : null);
        if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
        copiedTimer.current = window.setTimeout(() => {
            setCopiedToken(null);
            setCopyFailedToken(null);
        }, COPIED_RESET_MS);
    };

    const handleVariableClick = (placeholder: PlaceholderDefinition) => {
        onInsert(placeholder.token, placeholder.mandatory);

        navigator.clipboard
            .writeText(placeholder.token)
            .then(() => showCopyFeedback(placeholder.token, false))
            .catch(() => showCopyFeedback(placeholder.token, true));
    };

    const getChipSx = (placeholder: PlaceholderDefinition, justCopied: boolean) => {
        if (justCopied) {
            return { bgcolor: '#16a34a', color: '#fff', borderColor: '#16a34a' };
        }

        if (placeholder.mandatory) {
            return isVariableUsed(placeholder.token)
                ? { bgcolor: '#f0fdf4', color: '#15803d', borderColor: '#22c55e' }
                : { bgcolor: '#fef2f2', color: '#b91c1c', borderColor: '#ef4444' };
        }

        return { bgcolor: '#f9fafb', color: '#374151', borderColor: '#d1d5db' };
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
            }}
        >
            <Box component="span" aria-live="polite" sx={visuallyHiddenSx}>
                {copiedToken ? translate('placeholderCopied') : ''}
                {copyFailedToken ? translate('placeholderCopyFailed') : ''}
            </Box>
            {placeholders.map((placeholder) => {
                const isUsed = isVariableUsed(placeholder.token);
                const justCopied = copiedToken === placeholder.token;
                const copyFailed = copyFailedToken === placeholder.token;
                const showFeedback = justCopied || copyFailed;

                const title = showFeedback ? (
                    translate(justCopied ? 'placeholderCopied' : 'placeholderCopyFailed')
                ) : (
                    <Box>
                        <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
                            {placeholder.label}
                        </Box>
                        <Box
                            component="span"
                            sx={{ display: 'block', fontFamily: 'monospace', mt: 0.5 }}
                        >
                            {placeholder.token}
                        </Box>
                        {placeholder.description ? (
                            <Box component="span" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                                {placeholder.description}
                            </Box>
                        ) : null}
                        {placeholder.mandatory ? (
                            <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                                {translate('required')}
                            </Box>
                        ) : null}
                    </Box>
                );

                return (
                    <Tooltip
                        key={placeholder.token}
                        title={title}
                        arrow
                        placement="top"
                        open={showFeedback ? true : undefined}
                        disableHoverListener={showFeedback}
                        disableFocusListener={showFeedback}
                    >
                        <Chip
                            clickable
                            size="small"
                            aria-label={placeholder.label}
                            onClick={() => handleVariableClick(placeholder)}
                            label={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        maxWidth: 160,
                                    }}
                                >
                                    {placeholder.mandatory ? (
                                        isUsed || justCopied ? (
                                            <Star
                                                sx={{
                                                    fontSize: 12,
                                                    color: justCopied ? '#fff' : '#16a34a',
                                                }}
                                            />
                                        ) : (
                                            <StarBorder sx={{ fontSize: 12, color: '#dc2626' }} />
                                        )
                                    ) : null}
                                    <Box
                                        component="span"
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {placeholder.label}
                                    </Box>
                                    {placeholder.mandatory && isUsed ? (
                                        <CheckCircleOutlined
                                            sx={{
                                                fontSize: 12,
                                                color: justCopied ? '#fff' : '#16a34a',
                                            }}
                                        />
                                    ) : null}
                                </Box>
                            }
                            sx={{
                                height: 28,
                                fontWeight: 500,
                                fontSize: 12,
                                borderWidth: 2,
                                borderStyle: 'solid',
                                ...getChipSx(placeholder, justCopied),
                            }}
                        />
                    </Tooltip>
                );
            })}
        </Box>
    );
}
